/**
 * Application constants
 */

import type { JobStatus } from '@/types/job';
import type { GridCellStatus } from '@/types/grid';

/**
 * API configuration
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
export const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';

/**
 * Pagination defaults
 */
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Job status display configuration
 */
export const JOB_STATUS_CONFIG: Record<JobStatus, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string;
}> = {
  pending: {
    label: 'Pending',
    color: '#1677ff',
    bgColor: '#e6f4ff',
    borderColor: '#91caff',
    icon: 'clock',
  },
  running: {
    label: 'Running',
    color: '#d48806',
    bgColor: '#fffbe6',
    borderColor: '#ffe58f',
    icon: 'loading',
  },
  paused: {
    label: 'Paused',
    color: '#722ed1',
    bgColor: '#f9f0ff',
    borderColor: '#d3adf7',
    icon: 'pause',
  },
  completed: {
    label: 'Completed',
    color: '#389e0d',
    bgColor: '#f6ffed',
    borderColor: '#b7eb8f',
    icon: 'check',
  },
  failed: {
    label: 'Failed',
    color: '#cf1322',
    bgColor: '#fff2f0',
    borderColor: '#ffccc7',
    icon: 'close',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#8c8c8c',
    bgColor: '#f5f5f5',
    borderColor: '#d9d9d9',
    icon: 'stop',
  },
};

/**
 * Grid cell status colors for map visualization
 */
export const CELL_STATUS_COLORS: Record<GridCellStatus, string> = {
  pending: '#1677ff',    // Blue - not yet dispatched
  queued: '#13c2c2',     // Cyan/Teal - in queue, waiting for worker
  processing: '#faad14', // Orange/amber - actively being scraped
  completed: '#52c41a',  // Green
  failed: '#ff4d4f',     // Red
  skipped: '#722ed1',    // Purple
};

/**
 * Cell status display configuration
 */
export const CELL_STATUS_CONFIG: Record<GridCellStatus, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  pending: {
    label: 'Pending',
    color: '#1677ff',
    bgColor: '#e6f4ff',
    borderColor: '#91caff',
  },
  queued: {
    label: 'Queued',
    color: '#08979c',
    bgColor: '#e6fffb',
    borderColor: '#87e8de',
  },
  processing: {
    label: 'Processing',
    color: '#d48806',
    bgColor: '#fffbe6',
    borderColor: '#ffe58f',
  },
  completed: {
    label: 'Completed',
    color: '#389e0d',
    bgColor: '#f6ffed',
    borderColor: '#b7eb8f',
  },
  failed: {
    label: 'Failed',
    color: '#cf1322',
    bgColor: '#fff2f0',
    borderColor: '#ffccc7',
  },
  skipped: {
    label: 'Skipped',
    color: '#722ed1',
    bgColor: '#f9f0ff',
    borderColor: '#d3adf7',
  },
};

/**
 * Price level display
 */
export const PRICE_LEVELS: Record<string, { label: string; symbol: string }> = {
  '$': { label: 'Inexpensive', symbol: '$' },
  '$$': { label: 'Moderate', symbol: '$$' },
  '$$$': { label: 'Expensive', symbol: '$$$' },
  '$$$$': { label: 'Very Expensive', symbol: '$$$$' },
};

/**
 * Grid configuration defaults
 */
export const GRID_DEFAULTS = {
  cellSizeKm: 1,
  zoomLevel: 16,
  overlapPercent: 10,
  minZoom: 14,
  maxZoom: 18,
};

/**
 * Map configuration
 */
export const MAP_DEFAULTS = {
  center: [52.52, 13.405] as [number, number], // Berlin - Leaflet expects [lat, lng] array
  initialZoom: 12,
  zoom: 12,
  minZoom: 3,
  maxZoom: 19,
  tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

/**
 * Popular cities for quick selection
 */
export const POPULAR_CITIES = [
  { name: 'Berlin', country: 'Germany', center: { lat: 52.52, lng: 13.405 } },
  { name: 'Paris', country: 'France', center: { lat: 48.8566, lng: 2.3522 } },
  { name: 'London', country: 'UK', center: { lat: 51.5074, lng: -0.1278 } },
  { name: 'New York', country: 'USA', center: { lat: 40.7128, lng: -74.006 } },
  { name: 'Amsterdam', country: 'Netherlands', center: { lat: 52.3676, lng: 4.9041 } },
  { name: 'Munich', country: 'Germany', center: { lat: 48.1351, lng: 11.582 } },
  { name: 'Vienna', country: 'Austria', center: { lat: 48.2082, lng: 16.3738 } },
  { name: 'Barcelona', country: 'Spain', center: { lat: 41.3851, lng: 2.1734 } },
] as const;

/**
 * Time constants
 */
export const TIME = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
} as const;

/**
 * Debounce delays
 */
export const DEBOUNCE = {
  SEARCH: 300,
  RESIZE: 100,
  SCROLL: 50,
} as const;

/**
 * Stale time for React Query caching
 */
export const STALE_TIME = {
  JOBS: 30 * TIME.SECOND,
  JOB_DETAILS: 10 * TIME.SECOND,
  PLACES: 60 * TIME.SECOND,
  STATS: 15 * TIME.SECOND,
  HEALTH: 10 * TIME.SECOND,
} as const;

/**
 * Export column defaults
 */
export const DEFAULT_EXPORT_COLUMNS = [
  'name',
  'address',
  'phone',
  'website',
  'rating',
  'reviewCount',
  'category',
  'latitude',
  'longitude',
  'googleMapsUrl',
] as const;

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  THEME: 'maps-scraper-theme',
  PREFERENCES: 'maps-scraper-preferences',
  RECENT_SEARCHES: 'maps-scraper-recent-searches',
  TABLE_COLUMNS: 'maps-scraper-table-columns',
  RESULTS_STATE: 'maps-scraper-results-state',
} as const;
